import { Grid, Divider, Box } from '@mui/material'
import { useSelector } from 'react-redux'
import React from 'react'
import { useStyles } from '../style'
import { UserListSlice } from '../../../reducers'
import { Info, Input } from '../components'
import { ActionButton } from '@emerald-works-nova/components'
import { capitalize } from 'lodash'
import RoleInput from './role-input'
import useDetailForm from './useDetailForm'
import useConsumerTenant from '../../consumer-list/useConsumerTenant'
import ConsumerToggle from '../../../components/toggle-user'
import { useDeleteUser } from '../../../hooks/useDeleteUser'
import { LoadingButton } from '@mui/lab'
import { CONSTANTS } from '../../../util/constants'
import { useNavigate } from 'react-router'
import { routes } from '../../../routes'
import { ALLOW_REMOVE_B2C_USER } from '../../../util/permissions'
import { UserGroupPolicy } from '../../../components/user-group-policy'

const formatDate = date => new Date(date).toLocaleDateString()

const DetailTab = () => {
  const classes = useStyles()

  const navigate = useNavigate()

  const { deleteUser, isDeleting } = useDeleteUser({
    onDeleteSuccess: () => {
      navigate(routes.consumerList.path)
    }
  })

  const user = useSelector(UserListSlice.selectors.selectUser)
  const { detail } = user
  const userPoolId = useConsumerTenant()

  const handleDeleteUser = () => {
    deleteUser({
      username: detail.username,
      tenantId: CONSTANTS.DEFAULT_CLUB_PK
    })
  }

  const {
    givenName,
    familyName,
    roles,
    username,
    email,
    createdAt,
    lastAccess
  } = detail ?? {}

  const {
    isEdit,
    control,
    formState: { isDirty },
    onSubmit,
    handleSubmit,
    handleEdit,
    handleCancelEdit,
    loading
  } = useDetailForm()

  return (
    <form>
      <Grid
        container
        direction='column'
        spacing={3}
        className={classes.tabPanel}
      >
        <Grid container item>
          <Info title='First Name'>
            {isEdit ? (
              <Input name='givenName' data-test='first-name-text-field' control={control} />
            ) : (
              givenName
            )}
          </Info>
          <Info title='Last Name'>
            {isEdit ? (
              <Input name='familyName' data-test='last-name-text-field' control={control} />
            ) : (
              familyName
            )}
          </Info>
          <Info title='Role'>
            {isEdit ? (
              <RoleInput control={control} />
            ) : (
              capitalize(roles?.[0] ?? 'No role')
            )}
          </Info>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid container item>
          <Info title='Member ID'>{username}</Info>
          <Info title='Email Address'>
            {isEdit ? (
              <Input name='email' data-test='email-text-field' control={control} />
            ) : (
              email
            )}
          </Info>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid container item>
          <Info title='Creation Date'>{formatDate(createdAt)}</Info>
          <Info title='Last Access'>
            {lastAccess ? formatDate(lastAccess) : "Hasn't signed in"}
          </Info>
          <Info title='User Status'>
            <Box mt={-1.5}>
              <ConsumerToggle user={detail} userPoolId={userPoolId} />
            </Box>
          </Info>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid container spacing={2} item xs={12}>
          {isEdit ? (
            <>
              <Grid item>
                <ActionButton
                  onClick={handleCancelEdit}
                  variant='secondary'
                  dataTest='cancel-btn'
                >
                  Cancel
                </ActionButton>
              </Grid>
              <Grid item>
                <ActionButton
                  onClick={handleSubmit(onSubmit)}
                  variant='primary'
                  isWorking={loading}
                  disabled={!isDirty}
                  dataTest='save-changes-btn'
                >
                  Save changes
                </ActionButton>
              </Grid>
            </>
          ) : (
            <Grid item sx={{ display: 'flex', flex: 1, alignItems: 'center', justifyContent: 'space-between' }}>
              <ActionButton
                onClick={handleEdit}
                variant='secondary'
                dataTest='edit-user-details-btn'
              >
                Edit user details
              </ActionButton>
              <UserGroupPolicy
                accessGroup={ALLOW_REMOVE_B2C_USER}
                componentAllow={
                  <LoadingButton
                    variant='outlined'
                    color='error'
                    loading={isDeleting}
                    onClick={handleDeleteUser}
                    data-test='delete-user-btn'
                  >
                    Delete this user
                  </LoadingButton>
                }
              />

            </Grid>
          )}
        </Grid>
      </Grid>
    </form>
  )
}

export default DetailTab
